import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetBillingStatusQueryKey,
  useCreateBillingCheckout,
  useCreateBillingPortal,
  useGetBillingStatus,
} from "@workspace/api-client-react";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CreditCard,
  Loader2,
  Sparkles,
  XCircle,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";

const plans = [
  {
    name: "Starter",
    price: "$15",
    period: "per month",
    description: "For small businesses improving one website.",
    features: [
      "1 website",
      "Accessibility visitor controls",
      "Common issue monitoring",
      "Accessibility statement generator",
      "Step-by-step setup guidance",
    ],
    isAgency: false,
  },
  {
    name: "Agency",
    price: "Custom",
    period: "",
    description: "For agencies managing a growing client roster.",
    features: [
      "Everything in Starter",
      "Multiple websites in one account",
      "Dashboard overview across sites",
      "Shareable remediation reports",
    ],
    isAgency: true,
  },
];

type CheckoutMessage = {
  type: "success" | "cancelled";
  text: string;
};

export default function BillingPage() {
  const queryClient = useQueryClient();
  const {
    data: billingStatus,
    isLoading,
    isError,
  } = useGetBillingStatus();
  const checkoutMutation = useCreateBillingCheckout();
  const portalMutation = useCreateBillingPortal();
  const [checkoutMessage, setCheckoutMessage] =
    useState<CheckoutMessage | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const checkoutState = searchParams.get("checkout");

    if (checkoutState === "success") {
      setCheckoutMessage({
        type: "success",
        text: "Payment completed. Your Starter status is being updated.",
      });
      void queryClient.invalidateQueries({
        queryKey: getGetBillingStatusQueryKey(),
      });
    } else if (checkoutState === "cancelled") {
      setCheckoutMessage({
        type: "cancelled",
        text: "Checkout was cancelled. Your account has not been charged.",
      });
    } else {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("checkout");
    window.history.replaceState({}, "", url);
  }, [queryClient]);

  const handleCheckout = () => {
    setCheckoutMessage(null);
    checkoutMutation.mutate(undefined, {
      onSuccess: (data) => window.location.assign(data.url),
    });
  };

  const handlePortal = () => {
    portalMutation.mutate(undefined, {
      onSuccess: (data) => window.location.assign(data.url),
    });
  };

  const isStarterActive =
    billingStatus?.isActive && billingStatus.plan === "starter";

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl p-5 sm:p-8">
        <div className="mb-10">
          <h1 className="mb-3 text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Billing
          </h1>
          <p className="text-lg font-medium text-muted-foreground">
            Manage your subscription and billing preferences
          </p>
        </div>

        {checkoutMessage && (
          <div
            role="status"
            className={`mb-8 flex items-start gap-3 rounded-xl border p-4 shadow-sm ${
              checkoutMessage.type === "success"
                ? "border-[#22C55E]/20 bg-[#22C55E]/10"
                : "border-destructive-border bg-destructive/10"
            }`}
          >
            {checkoutMessage.type === "success" ? (
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            )}
            <p
              className={`font-semibold ${
                checkoutMessage.type === "success"
                  ? "text-[#16A34A] dark:text-[#22C55E]"
                  : "text-destructive"
              }`}
            >
              {checkoutMessage.text}
            </p>
          </div>
        )}

        {isError && (
          <div
            role="alert"
            className="mb-8 rounded-xl border border-destructive-border bg-destructive/5 p-6 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-destructive" />
              <div>
                <h2 className="mb-1 text-lg font-bold tracking-tight text-foreground">
                  Could not load billing status
                </h2>
                <p className="font-medium text-muted-foreground">
                  Refresh the page and try again. You will not be charged unless
                  Stripe checkout is completed.
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="mb-8 flex items-center gap-4 rounded-xl border border-card-border bg-card p-8 shadow-[var(--shadow-card)]">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="font-medium text-muted-foreground">
              Loading billing details...
            </p>
          </div>
        ) : (
          <div
            className={`relative mb-10 overflow-hidden rounded-xl border p-6 shadow-[var(--shadow-card)] sm:p-8 ${
              isStarterActive
                ? "border-primary/30 bg-primary/5"
                : "border-card-border bg-card"
            }`}
          >
            <div className="relative z-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div className="flex items-start gap-4">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${
                    isStarterActive
                      ? "border-primary/20 bg-primary/10"
                      : "border-border bg-muted"
                  }`}
                >
                  {isStarterActive ? (
                    <Sparkles className="h-5 w-5 text-primary" />
                  ) : (
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <h2 className="mb-1 text-xl font-bold tracking-tight text-foreground">
                    {isStarterActive ? "Starter plan active" : "No paid plan"}
                  </h2>
                  <p className="font-medium text-muted-foreground">
                    {isStarterActive
                      ? billingStatus.cancelAtPeriodEnd
                        ? `Access continues until ${
                            billingStatus.currentPeriodEnd
                              ? new Date(
                                  billingStatus.currentPeriodEnd,
                                ).toLocaleDateString()
                              : "the end of this billing period"
                          }.`
                        : `Next billing date: ${
                            billingStatus.currentPeriodEnd
                              ? new Date(
                                  billingStatus.currentPeriodEnd,
                                ).toLocaleDateString()
                              : "shown in the billing portal"
                          }.`
                      : "Choose Starter below to begin a secure Stripe checkout."}
                  </p>
                </div>
              </div>

              {isStarterActive && (
                <Button
                  variant="outline"
                  onClick={handlePortal}
                  disabled={portalMutation.isPending}
                  className="w-full bg-card font-bold shadow-sm md:w-auto"
                >
                  {portalMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="mr-2 h-4 w-4 text-primary" />
                  )}
                  Manage billing
                </Button>
              )}
            </div>
            {portalMutation.isError && (
              <p role="alert" className="relative z-10 mt-4 text-sm font-semibold text-destructive">
                Billing management could not be opened. Please try again.
              </p>
            )}
          </div>
        )}

        <div className="grid items-stretch gap-8 md:grid-cols-2">
          {plans.map((plan) => {
            const isCurrentPlan = Boolean(isStarterActive && !plan.isAgency);
            return (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-2xl border bg-card p-6 transition-all duration-300 sm:p-8 ${
                  isCurrentPlan
                    ? "border-primary/50 ring-1 ring-primary/20 shadow-[var(--shadow-elegant)]"
                    : "border-card-border shadow-[var(--shadow-card)]"
                }`}
              >
                {isCurrentPlan && (
                  <div className="absolute right-0 top-0 rounded-bl-xl rounded-tr-xl border-b border-l border-primary/20 bg-primary/10 px-4 py-1.5 text-xs font-extrabold tracking-wider text-primary">
                    CURRENT PLAN
                  </div>
                )}

                <h2 className="mb-2 text-2xl font-extrabold tracking-tight text-foreground">
                  {plan.name}
                </h2>
                <div className="mb-4 flex items-baseline">
                  <span className="text-5xl font-extrabold tracking-tight text-foreground">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="ml-2 font-semibold text-muted-foreground">
                      {plan.period}
                    </span>
                  )}
                </div>
                <p className="mb-8 text-base font-medium leading-relaxed text-muted-foreground">
                  {plan.description}
                </p>

                <ul className="mb-10 flex-grow space-y-4">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-3 text-base"
                    >
                      <div
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                          isCurrentPlan
                            ? "bg-primary/20 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <span className="font-medium text-foreground">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {plan.isAgency ? (
                  <Button
                    variant="outline"
                    className="mt-auto h-12 w-full border-2 border-dashed font-bold text-muted-foreground"
                    disabled
                  >
                    Agency inquiries coming soon
                  </Button>
                ) : isCurrentPlan ? (
                  <Button
                    variant="secondary"
                    className="mt-auto h-12 w-full font-bold"
                    disabled
                  >
                    Active subscription
                  </Button>
                ) : (
                  <div className="mt-auto">
                    <Button
                      onClick={handleCheckout}
                      disabled={
                        checkoutMutation.isPending || isLoading || isError
                      }
                      className="group h-12 w-full bg-primary text-base font-bold text-primary-foreground shadow-md"
                    >
                      {checkoutMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Starting checkout...
                        </>
                      ) : (
                        <>
                          Upgrade to Starter
                          <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </Button>
                    {checkoutMutation.isError && (
                      <p
                        role="alert"
                        className="mt-3 text-center text-sm font-semibold text-destructive"
                      >
                        Checkout could not be started. Please try again.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}